import { DateRule } from '../types'

// ---- Basic date helpers (all operate on local-time midnight dates) ----

export function toISODate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function fromISODate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function addDays(d: Date, n: number): Date {
  const copy = new Date(d)
  copy.setDate(copy.getDate() + n)
  return copy
}

export function daysInMonth(year: number, month0: number): number {
  return new Date(year, month0 + 1, 0).getDate()
}

export function isWeekend(d: Date): boolean {
  const day = d.getDay()
  return day === 0 || day === 6
}

export function isSameOrBefore(a: Date, b: Date): boolean {
  return a.getTime() <= b.getTime()
}

// ---- UK bank holidays (England & Wales) ----
// Approximation of the official calendar: fixed dates + Easter-derived dates,
// with the standard "moved to next available weekday" substitution rule for
// New Year's Day, Christmas Day and Boxing Day when they land on a weekend.
// For precise official dates (e.g. one-off holidays like coronations), cross
// check against gov.uk/bank-holidays.

function computeEasterSunday(year: number): Date {
  // Anonymous Gregorian algorithm (Meeus/Jones/Butcher)
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) // 3=March, 4=April
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

function nthWeekdayOfMonth(year: number, month0: number, weekday: number, n: number): Date {
  const first = new Date(year, month0, 1)
  const firstWeekday = first.getDay()
  const offset = (weekday - firstWeekday + 7) % 7
  const day = 1 + offset + (n - 1) * 7
  return new Date(year, month0, day)
}

function lastWeekdayOfMonth(year: number, month0: number, weekday: number): Date {
  const last = new Date(year, month0 + 1, 0)
  const lastWeekday = last.getDay()
  const offset = (lastWeekday - weekday + 7) % 7
  return new Date(year, month0, last.getDate() - offset)
}

function withSubstitutes(baseDates: Date[]): Date[] {
  const iso = (d: Date) => toISODate(d)
  const used = new Set<string>()
  const result: Date[] = []
  const sorted = [...baseDates].sort((a, b) => a.getTime() - b.getTime())

  for (const d of sorted) {
    if (!isWeekend(d)) {
      result.push(d)
      used.add(iso(d))
    }
  }
  for (const d of sorted) {
    if (isWeekend(d)) {
      let sub = addDays(d, 1)
      while (isWeekend(sub) || used.has(iso(sub))) {
        sub = addDays(sub, 1)
      }
      result.push(sub)
      used.add(iso(sub))
    }
  }
  return result
}

const holidayCache = new Map<number, Set<string>>()

export function getUkBankHolidays(year: number): Set<string> {
  const cached = holidayCache.get(year)
  if (cached) return cached

  const easter = computeEasterSunday(year)
  const goodFriday = addDays(easter, -2)
  const easterMonday = addDays(easter, 1)

  const withSubs = withSubstitutes([
    new Date(year, 0, 1), // New Year's Day
    new Date(year, 11, 25), // Christmas Day
    new Date(year, 11, 26), // Boxing Day
  ])

  const fixedWeekday = [
    goodFriday,
    easterMonday,
    nthWeekdayOfMonth(year, 4, 1, 1), // Early May bank holiday: 1st Monday in May
    lastWeekdayOfMonth(year, 4, 1), // Spring bank holiday: last Monday in May
    lastWeekdayOfMonth(year, 7, 1), // Summer bank holiday: last Monday in August
  ]

  const all = new Set<string>([...withSubs, ...fixedWeekday].map(toISODate))
  holidayCache.set(year, all)
  return all
}

export function isBankHoliday(d: Date): boolean {
  return getUkBankHolidays(d.getFullYear()).has(toISODate(d))
}

export function isWorkingDay(d: Date): boolean {
  return !isWeekend(d) && !isBankHoliday(d)
}

export function previousWorkingDay(d: Date): Date {
  let cur = new Date(d)
  while (!isWorkingDay(cur)) {
    cur = addDays(cur, -1)
  }
  return cur
}

export function nextWorkingDay(d: Date): Date {
  let cur = new Date(d)
  while (!isWorkingDay(cur)) {
    cur = addDays(cur, 1)
  }
  return cur
}

// ---- Resolving a DateRule to an actual calendar date for a given month ----

export function resolveDateRuleForMonth(rule: DateRule, year: number, month0: number): Date {
  const lastDay = daysInMonth(year, month0)
  const rawDay = rule.dayOfMonth === 'last' ? lastDay : Math.min(rule.dayOfMonth, lastDay)
  const raw = new Date(year, month0, rawDay)

  switch (rule.type) {
    case 'exact':
      return raw
    case 'working_day_before':
      return isWorkingDay(raw) ? raw : previousWorkingDay(raw)
    case 'working_day_after':
      return isWorkingDay(raw) ? raw : nextWorkingDay(raw)
  }
}
